import React, { useMemo } from 'react';
import { Skeleton } from 'src/components/ui/skeleton';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { HourlyDataPoint } from 'src/redux/admin/reporting/reporting.types';

interface HourlyChartProps {
  title: string;
  data: HourlyDataPoint[];
  metric: string;
  isLoading: boolean;
  spanCols?: number;
}

const HourlyChart: React.FC<HourlyChartProps> = ({ title, data, metric, isLoading, spanCols }) => {
  const aggregatedData = useMemo(() => {
    const hourlyTotals = new Array(24).fill(0).map((_, hour) => ({
      hour,
      [metric]: 0,
    }));

    data.forEach((point) => {
      const hour = parseInt(point.hour, 10);
      if (hour >= 0 && hour < 24) {
        const value = point[metric];
        if (typeof value === 'number') {
          hourlyTotals[hour][metric] += value;
        }
      }
    });

    return hourlyTotals;
  }, [data, metric]);

  const formatHour = (hour: number) => {
    return `${hour}:00`;
  };

  const formatHourAMPM = (hour: number) => {
    if (hour === 0) return '12:00 AM';
    if (hour === 12) return '12:00 PM';
    if (hour > 12) return `${hour - 12}:00 PM`;
    return `${hour}:00 AM`;
  };

  const formatMetricValue = (value: number): string => {
    if (!isFinite(value) || isNaN(value)) {
      return 'N/A';
    }
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toString();
  };

  if (isLoading) {
    return (
      <div
        className={`rounded-lg bg-white border border-gray-400 p-4 flex-1 h-[200px] flex flex-col overflow-visible ${
          spanCols ? `sm:col-span-${spanCols} lg:col-span-${spanCols} xl:col-span-${spanCols} 2xl:col-span-${spanCols}` : ''
        }`}
      >
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-sm font-medium text-gray-600">
            {title}
          </h3>
        </div>
        <Skeleton className="w-full h-[150px]" />
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg bg-white border border-gray-400 p-4 flex-1 h-[200px] flex flex-col overflow-visible ${
        spanCols ? `sm:col-span-${spanCols} lg:col-span-${spanCols} xl:col-span-${spanCols} 2xl:col-span-${spanCols}` : ''
      }`}
    >
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-sm font-medium text-gray-600">
          {title}
        </h3>
      </div>
      <div className="flex-1 min-h-0 w-full overflow-visible">
        <ResponsiveContainer
          width="100%"
          height="100%"
        >
          <BarChart
            data={aggregatedData}
            margin={{
              top: 5,
              right: 0,
              left: 0,
              bottom: 2,
            }}
          >
            <XAxis
              dataKey="hour"
              tickFormatter={formatHour}
              interval={1}
              tick={{ fontSize: 10 }}
              angle={45}
              textAnchor="start"
            />
            <YAxis
              tickFormatter={formatMetricValue}
              tick={{ fontSize: 10 }}
            />
            <Tooltip
              labelFormatter={formatHourAMPM}
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const value = payload[0].value;
                  return (
                    <div className="bg-white border border-gray-300 rounded-lg p-2 shadow-lg">
                      <p className="text-sm font-medium text-gray-900">
                        {formatHourAMPM(label)}
                      </p>
                      <p className="text-sm text-gray-600">
                        {"Impressions: "}
                        {
                          typeof value === 'number' ? formatMetricValue(value) : 'N/A'
                        }
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar
              dataKey={metric}
              fill="#8884d8"
              radius={[2, 2, 0, 0]}
              barSize={20}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default HourlyChart;